import { COMMAND_TOOLS, executeTool } from './commandTools';
import { getConfig } from './config';
import {
  enrichToolCallsFromCommand,
  NAMED_COLORS,
  inferColorFromCommand,
  ADDABLE_PARTICLE_TYPES,
} from './propertyHelpers';
import { normalizeModelSearchQuery } from './modelRelevance';
import {
  enrichSpatialToolCalls,
  enrichGroupLayoutToolCalls,
  enrichIndividuatedObjectToolCalls,
  enrichMultiTypeAddToolCalls,
  parseMultiTypeAddSpecs,
  parseSpatialRelation,
  parseQuantityFromText,
  parseGroupLayout,
} from './spatialLayout';
import {
  buildNormalizedPrompt,
  localNormalizeCommand,
} from './promptNormalizer';

// AI Interpreter using AWS Bedrock via backend API (Updated with Sketchfab support)
export class AIInterpreter {
  constructor(backendApiUrl = null) {
    const config = getConfig();
    this.backendApiUrl = backendApiUrl || config.backend.apiUrl;
    this.endpoint = config.backend.endpoint;

    // Define valid object types for validation
    this.validShapes = [
      'cube',
      'sphere',
      'cylinder',
      'cone',
      'pyramid',
      'capsule',
      'torus',
      'rectangle',
      'ellipse',
      'triangle',
      'plane',
    ];
    this.validEffects = [
      'rain',
      'snow',
      'fire',
      'stars',
      'clouds',
      'water',
      'foliage',
    ];
    this.validLights = [
      'pointlight',
      'directionallight',
      'spotlight',
      'arealight',
      'ambientlight',
    ];
    this.validContent = ['text', 'hotspot'];
    this.validSketchfabKeywords = [
      'model',
      '3d',
      'sketchfab',
      'download',
      'character',
      'car',
      'dragon',
      'house',
      'tree',
      'animal',
      'furniture',
    ];
    this.allValidTypes = [
      ...this.validShapes,
      ...this.validEffects,
      ...this.validLights,
      ...this.validContent,
      ...this.validSketchfabKeywords,
    ];
  }

  // Main method to interpret user command and execute tools
  async interpretAndExecute(userCommand, scene, transformControls, store) {
    try {
      // 1) Always normalize first (typos / slang / grammar cleanup)
      const pre = buildNormalizedPrompt(userCommand);
      const commandForAi = pre.normalized || pre.original;
      if (pre.changed) {
        console.info(
          '[automation] normalized prompt:',
          JSON.stringify(pre.original),
          '→',
          JSON.stringify(commandForAi)
        );
      }

      // 2) Get AI interpretation (Bedrock may further rewrite → normalizedCommand)
      let aiResponse;
      try {
        aiResponse = await this.interpretCommand(commandForAi, pre.original);
      } catch (apiError) {
        console.warn(
          'Bedrock interpret failed, using fallback parsing:',
          apiError.message
        );
        aiResponse = this.fallbackParsing(commandForAi);
        if (aiResponse.toolCalls?.length) {
          aiResponse = {
            ...aiResponse,
            toolCalls: enrichToolCallsFromCommand(
              commandForAi,
              aiResponse.toolCalls
            ),
          };
        }
      }

      // 3) Prefer AI rewrite when present, else local normalize
      const promptInfo = buildNormalizedPrompt(
        userCommand,
        aiResponse?.normalizedCommand
      );
      let workingCommand = promptInfo.normalized || commandForAi;

      // The AI rewrite often drops or duplicates per-shape details
      // ("2 cubes and 3 cones" → "2 cubes and 2 cones", or loses a rotation).
      // When the user's own wording already spells out counts per shape,
      // that wording is authoritative.
      const userSpecs = parseMultiTypeAddSpecs(commandForAi);
      if (userSpecs && userSpecs.length >= 2 && workingCommand !== commandForAi) {
        console.info(
          '[automation] compound multi-shape add — using user command over AI rewrite:',
          JSON.stringify(commandForAi)
        );
        workingCommand = commandForAi;
      }

      if (aiResponse.toolCalls && aiResponse.toolCalls.length > 0) {
        // "one is blue… other is red…" → rebuild as one call per distinct object
        let toolCalls = enrichIndividuatedObjectToolCalls(
          workingCommand,
          aiResponse.toolCalls
        );
        // "2 cubes and 3 cones …" → one call per shape with its own count
        toolCalls = enrichMultiTypeAddToolCalls(workingCommand, toolCalls);
        toolCalls = enrichToolCallsFromCommand(workingCommand, toolCalls);
        // Spatial relations: "cone on top of a cube"
        toolCalls = enrichSpatialToolCalls(workingCommand, toolCalls, scene);
        // Multi-object layouts: "five cylinders in a straight line"
        toolCalls = enrichGroupLayoutToolCalls(workingCommand, toolCalls);
        // Execute each tool call
        const results = [];
        for (const toolCall of toolCalls) {
          try {
            const result = await executeTool(
              toolCall.name,
              toolCall.parameters,
              scene,
              transformControls,
              store
            );
            results.push({ tool: toolCall.name, result });
          } catch (error) {
            results.push({ tool: toolCall.name, error: error.message });
          }
        }
        return {
          success: true,
          results,
          message: aiResponse.message,
          originalCommand: promptInfo.original,
          normalizedCommand: workingCommand,
        };
      } else {
        return { success: false, message: 'AI did not provide any tool calls' };
      }
    } catch (error) {
      console.error('AI interpretation error:', error);
      return { success: false, error: error.message };
    }
  }

  // Interpret user command using AWS Bedrock via backend API
  async interpretCommand(userCommand, originalCommand = null) {
    try {
      // Get auth token from localStorage if available
      const token = localStorage.getItem('token') || '';
      const headers = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.backendApiUrl}${this.endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userCommand,
          originalCommand: originalCommand || userCommand,
          commandTools: COMMAND_TOOLS,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Backend API error: ${response.status}`
        );
      }

      const data = await response.json();

      // Extract the aiResponse from the API response
      // The backend returns: { data: { aiResponse: { message, toolCalls } } }
      if (data.data && data.data.aiResponse) {
        return this.parseAIResponse(data.data.aiResponse);
      }

      // Fallback: try to parse directly
      return this.parseAIResponse(data);
    } catch (error) {
      console.error('Backend API error:', error);
      throw error;
    }
  }

  // Build prompt for tool calls with strict formatting rules
  buildToolCallPrompt(userCommand, originalCommand = null) {
    const toolsDescription = COMMAND_TOOLS.map(
      (tool) => `${tool.name}: ${tool.description}`
    ).join('\n');
    const raw = originalCommand || userCommand;

    return `
You are an AI assistant that helps users control a 3D scene editor. You have access to the following tools:

${toolsDescription}

IMPORTANT: Use EXACT parameter values from these lists:

SHAPES: cube, sphere, cylinder, cone, pyramid, capsule, torus, rectangle, ellipse, triangle, plane
EFFECTS: rain, snow, fire, stars, clouds, water, foliage (do NOT use terrain)
LIGHTS: pointlight, directionallight, spotlight, arealight, ambientlight
TEXT / HOTSPOT: Prefer tools "add_text" and "add_hotspot" (or objectType "text" / "hotspot" on add_object)
SKETCHFAB / LIBRARY 3D MODELS: Use "add_sketchfab_model" or "add_3d_model" (library first, then Sketchfab)

STEP 0 — NORMALIZE THE PROMPT FIRST (mandatory):
Users type messy prompts: typos, bad grammar, slang, missing words, mixed order.
1. Rewrite the user input into ONE clear, correct English imperative sentence for a 3D scene editor.
2. Fix spelling/grammar/slang. Keep the SAME intent (counts, colors, rotations, "all", layouts, spatial relations, text content).
3. Do NOT add objects, properties, or actions the user did not ask for.
4. Prefer short canonical forms, e.g.:
   - "pls ad 5 blue sylinder stright line verticle" → "add 5 blue cylinders in a straight line vertically"
   - "make all cub oragne" → "make all cubes orange"
   - "torus rotatd 90 deg on x axsis" → "add a torus rotated 90° on X axis"
   - "gimme cone ontop cube" → "add a cone on top of a cube"
5. Put that rewrite in "normalizedCommand". Then produce toolCalls FROM normalizedCommand ONLY.

CRITICAL RULES:
1. Use ONLY the exact names from the lists above
2. Do NOT add suffixes like "_effect" or modify the names
3. If user says "rain effect" or "add rain", use objectType: "rain" (not "rain_effect")
4. If user says "point light", use objectType: "pointlight" (not "point_light")
5. All object type names must be lowercase and single words
6. For 3D models, characters, vehicles, buildings, animals, etc., use "add_sketchfab_model" (or "add_3d_model") — the system searches the SpatialGrid library first, then Sketchfab
7. Sketchfab/library queries should be descriptive: "car", "dragon", "house", "tree", "character", etc.
8. When the user specifies appearance (color, dim/bright, heavy/sparse, size/scale, opacity/transparent, metalness/metallic/chrome, roughness/matte, emission/glow, wireframe), include those fields on add_object (color as #RRGGBB hex; opacity/metalness/roughness 0–1). Prefer create-with-properties over a separate modify_object call. "opacity 0" / "fully transparent" → opacity 0.
9. For text, always set content to the exact words to display. Use add_text with color/fontSize/textType when given (e.g. "add blue text Hello").
10. For hotspots, use add_hotspot with buttonText and messageText.
11. SPATIAL RELATIONS: For phrases like "on top of", "above", "below", "left of", "right of", "next to", "in front of", "behind":
    - Emit TWO add_object calls: first the anchor, then the subject.
    - On the subject call set relativeTo to the anchor objectType and placement to one of: on_top, above, below, left, right, front, behind, next_to.
    - Example: "add a cone on top of a cube" → add cube, then add cone with relativeTo "cube" and placement "on_top".
    - Do NOT put both at the same position.
12. MULTI-OBJECT LAYOUT: Whenever quantity > 1, ALWAYS set layout so objects are spaced apart (never the same coordinates):
    - "in a straight line" / "in a row" → layout "line", layoutAxis "x" (default horizontal)
    - "straight line vertically" / "vertical line" / "in a column" → layout "line", layoutAxis "y"
    - "front to back" / "along z" → layout "line", layoutAxis "z"
    - "in a circle" → layout "circle"
    - "in a grid" → layout "grid"
    - "stacked" / "on top of each other" → layout "stack"
    - If the user only says "add 5 cubes" with no arrangement, still use layout "line".
    - Word quantities: "five" → quantity 5. Orientation words always override the default axis.
13. ROTATION vs QUANTITY: Numbers with degrees (90°, 45 deg) are rotation, NOT quantity. Never set quantity to an angle.
    - Named axis: "on X axis" → rotation {{"x":90}}. pitch→x, yaw→y, roll→z.
    - NO axis given ("rotated 45deg", "-45deg rotated") → apply that angle to ALL three axes: {{"x":45,"y":45,"z":45}}.
14. MODIFY ALL: When the user says "all", "every", or uses a plural ("make all cubes orange", "make the spheres red"), set all=true on modify_object so every matching object is updated — not only the first.
15. ALL PARTICLES / EFFECTS: When the user says "add all particles" or "add all effects", emit one add_object call per effect in EFFECTS only (rain, snow, fire, stars, clouds, water, foliage). Never include terrain.
16. DISTINCT INDIVIDUALS: For "add two cubes, one is blue … the other is red …", emit TWO add_object calls each with quantity 1 and different properties — NEVER quantity 2 on each call (that creates 4 objects).
17. MULTI-TYPE ADDS: For "add 2 cubes … and 3 cones …", emit SEPARATE add_object calls with the correct per-type quantity, color, and rotation — never copy the first count onto every type.

Original user input: "${raw}"
Pre-cleaned command: "${userCommand}"

Respond with ONLY a JSON object in this exact format:

{
  "normalizedCommand": "Clear corrected imperative sentence",
  "message": "Brief explanation of what you're going to do",
  "toolCalls": [
    {
      "name": "tool_name",
      "parameters": {
        "objectType": "EXACT_NAME_FROM_LISTS_ABOVE",
        "quantity": 1
      }
    }
  ]
}

Examples (normalizedCommand + tools):
- "add 3 spheres" → {"normalizedCommand":"add 3 spheres","message":"Adding 3 spheres to the scene","toolCalls":[{"name":"add_object","parameters":{"objectType":"sphere","quantity":3,"layout":"line"}}]}
- "pls ad 5 blue sylinder stright line verticle" → {"normalizedCommand":"add 5 blue cylinders in a straight line vertically","message":"Adding 5 blue cylinders in a vertical line","toolCalls":[{"name":"add_object","parameters":{"objectType":"cylinder","quantity":5,"color":"#0000FF","layout":"line","layoutAxis":"y"}}]}
- "make all cub oragne" → {"normalizedCommand":"make all cubes orange","message":"Making all cubes orange","toolCalls":[{"name":"modify_object","parameters":{"objectType":"cube","color":"#FFA500","all":true}}]}
- "torus rotatd 90 deg on x axsis" → {"normalizedCommand":"add a torus rotated 90° on X axis","message":"Adding a torus rotated 90° on X","toolCalls":[{"name":"add_object","parameters":{"objectType":"torus","quantity":1,"rotation":{"x":90,"y":0,"z":0}}}]}
- "add rain effect" → {"normalizedCommand":"add rain","message":"Adding rain effect","toolCalls":[{"name":"add_object","parameters":{"objectType":"rain","quantity":1}}]}
- "add blue rain" → {"normalizedCommand":"add blue rain","message":"Adding blue rain","toolCalls":[{"name":"add_object","parameters":{"objectType":"rain","color":"#0000FF"}}]}
- "heavy snow" → {"normalizedCommand":"add heavy snow","message":"Adding heavy snow","toolCalls":[{"name":"add_object","parameters":{"objectType":"snow","particleCount":2500}}]}
- "add 3 red cubes" → {"normalizedCommand":"add 3 red cubes","message":"Adding 3 red cubes","toolCalls":[{"name":"add_object","parameters":{"objectType":"cube","quantity":3,"color":"#FF0000","layout":"line"}}]}
- "add a dark purple cube" → {"normalizedCommand":"add a dark purple cube","message":"Adding a dark purple cube","toolCalls":[{"name":"add_object","parameters":{"objectType":"cube","color":"#4B0082"}}]}
- "add a transparent cube with opacity 0" → {"normalizedCommand":"add a transparent cube with opacity 0","message":"Adding a fully transparent cube","toolCalls":[{"name":"add_object","parameters":{"objectType":"cube","opacity":0}}]}
- "add two cubes one is blue -45deg rotated and other is red 45deg rotated" → {"normalizedCommand":"add two cubes: one blue rotated -45° on all axes, the other red rotated 45° on all axes","message":"Adding a blue cube and a red cube with opposite rotations","toolCalls":[{"name":"add_object","parameters":{"objectType":"cube","quantity":1,"color":"#0000FF","rotation":{"x":-45,"y":-45,"z":-45}}},{"name":"add_object","parameters":{"objectType":"cube","quantity":1,"color":"#FF0000","rotation":{"x":45,"y":45,"z":45}}}]}
- "add 2 cubes of dark blue color and 3 cones of dark purple color, all the cubes should be rotated by 45° and all the cones should be rotated by 90°" → {"normalizedCommand":"add 2 dark blue cubes rotated 45° on all axes and 3 dark purple cones rotated 90° on all axes","message":"Adding 2 dark blue cubes and 3 dark purple cones","toolCalls":[{"name":"add_object","parameters":{"objectType":"cube","quantity":2,"color":"#00008B","rotation":{"x":45,"y":45,"z":45},"layout":"line"}},{"name":"add_object","parameters":{"objectType":"cone","quantity":3,"color":"#4B0082","rotation":{"x":90,"y":90,"z":90},"layout":"line"}}]}
- "create point light" → {"normalizedCommand":"add a point light","message":"Adding point light","toolCalls":[{"name":"add_object","parameters":{"objectType":"pointlight","quantity":1}}]}
- "remove all cubes" → {"normalizedCommand":"remove all cubes","message":"Removing all cubes from the scene","toolCalls":[{"name":"remove_object","parameters":{"objectType":"cube"}}]}
- "add a car model" → {"normalizedCommand":"add a car model","message":"Adding a car from library or Sketchfab","toolCalls":[{"name":"add_sketchfab_model","parameters":{"query":"car"}}]}
- "add text Hello World" → {"normalizedCommand":"add text Hello World","message":"Adding text Hello World","toolCalls":[{"name":"add_text","parameters":{"content":"Hello World","textType":"3D"}}]}
- "gimme cone ontop cube" → {"normalizedCommand":"add a cone on top of a cube","message":"Adding a cube with a cone on top","toolCalls":[{"name":"add_object","parameters":{"objectType":"cube"}},{"name":"add_object","parameters":{"objectType":"cone","relativeTo":"cube","placement":"on_top"}}]}
- "make the cube red" → {"normalizedCommand":"make the cube red","message":"Making the cube red","toolCalls":[{"name":"modify_object","parameters":{"objectType":"cube","color":"#FF0000"}}]}
- "help with shapes" → {"normalizedCommand":"help with shapes","message":"Showing help for shapes","toolCalls":[{"name":"show_help","parameters":{"category":"shapes"}}]}

JSON response:`;
  }

  // Normalize object type to match valid names
  normalizeObjectType(objectType) {
    if (!objectType) return objectType;

    // Direct match (case insensitive)
    const lowerType = objectType.toLowerCase();
    if (this.allValidTypes.includes(lowerType)) {
      return lowerType;
    }

    // Plurals: cylinders → cylinder, cubes → cube
    const singular = lowerType.replace(/s$/, '');
    if (singular !== lowerType && this.allValidTypes.includes(singular)) {
      return singular;
    }
    const ies = lowerType.replace(/ies$/, 'y');
    if (ies !== lowerType && this.allValidTypes.includes(ies)) {
      return ies;
    }

    // Common corrections mapping
    const corrections = {
      rain_effect: 'rain',
      raineffect: 'rain',
      snow_effect: 'snow',
      snoweffect: 'snow',
      fire_effect: 'fire',
      fireeffect: 'fire',
      stars_effect: 'stars',
      starseffect: 'stars',
      clouds_effect: 'clouds',
      cloudseffect: 'clouds',
      water_effect: 'water',
      watereffect: 'water',
      foliage_effect: 'foliage',
      foliageeffect: 'foliage',
      point_light: 'pointlight',
      pointLight: 'pointlight',
      directional_light: 'directionallight',
      directionalLight: 'directionallight',
      spot_light: 'spotlight',
      spotLight: 'spotlight',
      area_light: 'arealight',
      areaLight: 'arealight',
      ambient_light: 'ambientlight',
      ambientLight: 'ambientlight',
    };

    if (corrections[objectType]) {
      return corrections[objectType];
    }

    if (corrections[lowerType]) {
      return corrections[lowerType];
    }

    // Fuzzy matching - remove common suffixes/prefixes
    const normalized = lowerType
      .replace(/_effect$/, '')
      .replace(/effect$/, '')
      .replace(/^add_/, '')
      .replace(/^create_/, '')
      .replace(/^make_/, '')
      .replace(/_light$/, 'light')
      .replace(/light$/, 'light')
      .replace(/_/, '')
      .replace(/\s+/g, '');

    // Find closest match
    const match = this.allValidTypes.find(
      (valid) =>
        valid === normalized ||
        valid.includes(normalized) ||
        normalized.includes(valid.replace('light', ''))
    );

    if (match) {
      return match;
    }

    console.warn(`Could not normalize object type: "${objectType}"`);
    return objectType; // Return original if no match found
  }

  // Parse AI response and extract tool calls with validation and normalization
  parseAIResponse(aiResponse) {
    try {
      // If aiResponse is already a parsed object, use it directly
      let parsed;
      if (typeof aiResponse === 'string') {
        // Extract JSON from AI response string
        const jsonMatch = aiResponse.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
          throw new Error('No JSON found in AI response');
        }
        parsed = JSON.parse(jsonMatch[0]);
      } else {
        // Already parsed object
        parsed = aiResponse;
      }

      // Validate response structure
      if (!parsed.toolCalls || !Array.isArray(parsed.toolCalls)) {
        throw new Error('Invalid response structure: missing toolCalls array');
      }

      if (parsed.normalizedCommand != null) {
        parsed.normalizedCommand = String(parsed.normalizedCommand).trim();
      }

      // Validate and normalize each tool call
      parsed.toolCalls = parsed.toolCalls.map((toolCall) => {
        // Models sometimes use `arguments` instead of `parameters`
        if (!toolCall.parameters && toolCall.arguments) {
          toolCall.parameters =
            typeof toolCall.arguments === 'string'
              ? JSON.parse(toolCall.arguments)
              : toolCall.arguments;
        }
        if (!toolCall.parameters || typeof toolCall.parameters !== 'object') {
          toolCall.parameters = {};
        }
        if (!toolCall.name) {
          throw new Error('Invalid tool call structure: missing name');
        }

        // Validate tool name exists
        const validTools = COMMAND_TOOLS.map((t) => t.name);
        if (!validTools.includes(toolCall.name)) {
          throw new Error(`Unknown tool: ${toolCall.name}`);
        }

        // Normalize objectType parameter if present (for add_object tool)
        if (toolCall.name === 'add_object' && toolCall.parameters.objectType) {
          toolCall.parameters.objectType = this.normalizeObjectType(
            toolCall.parameters.objectType
          );
        }

        // Soft-normalize modify_object: map common aliases into a usable shape
        if (toolCall.name === 'modify_object') {
          const p = toolCall.parameters;
          if (!p.objectType && p.type) p.objectType = p.type;
          if (!p.name && (p.objectName || p.query || p.targetName)) {
            p.name = p.objectName || p.query || p.targetName;
          }
          // If AI only said "cube" / "Cube" as name
          if (!p.objectType && p.name) {
            p.objectType = this.normalizeObjectType(p.name) || p.name;
          }
          if (p.objectType) {
            p.objectType = this.normalizeObjectType(p.objectType) || p.objectType;
          }
        }

        // Validate / sanitize model search queries
        if (
          toolCall.name === 'add_sketchfab_model' ||
          toolCall.name === 'add_3d_model'
        ) {
          if (!toolCall.parameters.query) {
            throw new Error('3D model tool requires a search query');
          }
          const cleaned =
            normalizeModelSearchQuery(toolCall.parameters.query) ||
            String(toolCall.parameters.query).trim();
          if (!cleaned || cleaned.length < 2) {
            throw new Error('3D model search query too short');
          }
          toolCall.parameters.query = cleaned;
        }

        return toolCall;
      }).filter((toolCall) => {
        // Terrain is disabled for create — never add it (including "all particles")
        if (toolCall?.name !== 'add_object') return true;
        const t = String(toolCall.parameters?.objectType || '').toLowerCase();
        return t !== 'terrain';
      });

      return parsed;
    } catch (parseError) {
      console.error('Failed to parse AI response:', parseError);
      throw new Error(`AI response parsing failed: ${parseError.message}`);
    }
  }

  // Enhanced fallback parsing with Sketchfab support
  fallbackParsing(userCommand) {
    const original = String(userCommand || '');
    const lowerCommand = localNormalizeCommand(original).toLowerCase() ||
      original.toLowerCase();
    const commandText = localNormalizeCommand(original) || original;

    // Spatial composition: "cone on top of a cube" etc.
    const spatial = parseSpatialRelation(commandText);
    if (spatial) {
      return {
        message: `Adding ${spatial.anchor} with ${spatial.subject} ${spatial.placement.replace(/_/g, ' ')}`,
        toolCalls: [
          {
            name: 'add_object',
            parameters: { objectType: spatial.anchor, quantity: 1 },
          },
          {
            name: 'add_object',
            parameters: {
              objectType: spatial.subject,
              quantity: 1,
              relativeTo: spatial.anchor,
              placement: spatial.placement,
            },
          },
        ],
      };
    }

    // "add 2 cubes and 3 cones …" — one call per shape with its own count
    const multiSpecs = parseMultiTypeAddSpecs(commandText);
    if (multiSpecs && multiSpecs.length >= 2) {
      return {
        message: `Adding ${multiSpecs
          .map((s) => `${s.quantity} ${s.objectType}${s.quantity > 1 ? 's' : ''}`)
          .join(' and ')}`,
        toolCalls: multiSpecs.map((spec) => ({
          name: 'add_object',
          parameters: { ...spec },
        })),
      };
    }

    // "add all particles" / "add all effects" — every addable effect except terrain
    if (
      /\b(add|create|make|spawn)\b/.test(lowerCommand) &&
      /\ball\b/.test(lowerCommand) &&
      /\b(particles?|effects?)\b/.test(lowerCommand)
    ) {
      return {
        message: `Adding all particle effects (${ADDABLE_PARTICLE_TYPES.join(', ')})`,
        toolCalls: ADDABLE_PARTICLE_TYPES.map((objectType) => ({
          name: 'add_object',
          parameters: { objectType, quantity: 1 },
        })),
      };
    }

    // Help command
    if (lowerCommand.includes('help')) {
      let category = 'general';
      if (lowerCommand.includes('text')) category = 'text';
      else if (lowerCommand.includes('hotspot') || lowerCommand.includes('hot spot'))
        category = 'hotspot';
      else if (lowerCommand.includes('shape')) category = 'shapes';
      else if (lowerCommand.includes('light')) category = 'lights';
      else if (lowerCommand.includes('effect') || lowerCommand.includes('particle'))
        category = 'effects';
      else if (lowerCommand.includes('model') || lowerCommand.includes('sketchfab'))
        category = 'models';
      return {
        message: `Showing help for ${category}`,
        toolCalls: [{ name: 'show_help', parameters: { category } }],
      };
    }

    // Text — before Sketchfab so "3d text" is not treated as a model search
    if (
      /\btext\b/.test(lowerCommand) &&
      (/\b(add|create|make|write|put)\b/.test(lowerCommand) ||
        lowerCommand.startsWith('text '))
    ) {
      const content = this.extractTextContentFallback(original);
      const params = {
        content: content || 'Spatial Grid',
        textType: /\b2d\b/.test(lowerCommand) ? '2D' : '3D',
      };
      return {
        message: `Adding text "${params.content}"`,
        toolCalls: [{ name: 'add_text', parameters: params }],
      };
    }

    // Hotspot
    if (
      /\bhot\s*-?\s*spot\b/.test(lowerCommand) &&
      /\b(add|create|make|put)\b/.test(lowerCommand)
    ) {
      const hs = this.extractHotspotFallback(original);
      return {
        message: `Adding hotspot "${hs.buttonText}"`,
        toolCalls: [{ name: 'add_hotspot', parameters: hs }],
      };
    }

    // Sketchfab model commands (skip when clearly text/hotspot)
    const sketchfabKeywords = [
      'model',
      '3d',
      'sketchfab',
      'download',
      'get',
      'glb',
      'gltf',
      'mesh',
    ];
    const modelTypes = [
      'car',
      'dragon',
      'house',
      'tree',
      'character',
      'animal',
      'furniture',
      'building',
      'vehicle',
    ];

    if (
      (sketchfabKeywords.some((keyword) => lowerCommand.includes(keyword)) ||
        modelTypes.some((type) => lowerCommand.includes(type))) &&
      !/\btext\b/.test(lowerCommand) &&
      !/\bhot\s*-?\s*spot\b/.test(lowerCommand)
    ) {
      // Prefer known subjects (car, house, …) — never overwrite with filler like "scene"
      let query = '';
      for (const type of modelTypes) {
        if (new RegExp(`\\b${type}\\b`, 'i').test(lowerCommand)) {
          query = type;
          break;
        }
      }

      if (!query) {
        const words = lowerCommand.split(/[^a-z0-9]+/).filter(Boolean);
        const skip = new Set([
          'add',
          'create',
          'make',
          'get',
          'download',
          'a',
          'an',
          'the',
          'from',
          'sketchfab',
          'model',
          'models',
          '3d',
          'glb',
          'gltf',
          'mesh',
          'please',
          'to',
          'into',
          'onto',
          'in',
          'on',
          'my',
          'scene',
          'grid',
          'object',
          'asset',
          'file',
          'some',
          'new',
          'one',
        ]);
        const meaningfulWords = words.filter(
          (word) => !skip.has(word) && word.length > 2
        );
        query =
          meaningfulWords.length > 0
            ? meaningfulWords.join(' ')
            : 'glb';
      }

      query = normalizeModelSearchQuery(query) || query;

      return {
        message: `Searching and adding ${query} model`,
        toolCalls: [
          {
            name: 'add_sketchfab_model',
            parameters: { query },
          },
        ],
      };
    }

    // Add commands
    if (
      lowerCommand.includes('add') ||
      lowerCommand.includes('create') ||
      lowerCommand.includes('make')
    ) {
      const skipWords = new Set([
        'a',
        'an',
        'the',
        'some',
        'add',
        'create',
        'make',
        'effect',
        'effects',
        'model',
        'dim',
        'bright',
        'heavy',
        'sparse',
        'few',
        'gentle',
        'big',
        'large',
        'huge',
        'small',
        'tiny',
        'soft',
        'strong',
        'intense',
        'high',
        'low',
        ...Object.keys(NAMED_COLORS).map((k) => k.toLowerCase()),
      ]);

      // Prefer matching a known object type anywhere in the phrase
      const words = lowerCommand.split(/[^a-z0-9]+/).filter(Boolean);
      let foundType = null;
      for (const word of words) {
        if (skipWords.has(word)) continue;
        const normalized = this.normalizeObjectType(word);
        if (this.allValidTypes.includes(normalized)) {
          foundType = normalized;
          break;
        }
      }

      // Lights written as two words: "point light", "spot light", etc.
      if (!foundType) {
        if (lowerCommand.includes('point light')) foundType = 'pointlight';
        else if (lowerCommand.includes('directional light'))
          foundType = 'directionallight';
        else if (lowerCommand.includes('spot light')) foundType = 'spotlight';
        else if (lowerCommand.includes('area light')) foundType = 'arealight';
        else if (lowerCommand.includes('ambient light'))
          foundType = 'ambientlight';
      }

      if (foundType) {
        const quantity = parseQuantityFromText(commandText) || 1;
        const group = parseGroupLayout(commandText, quantity);
        const params = {
          objectType: foundType,
          quantity: Number.isFinite(quantity) ? quantity : 1,
        };
        if (params.quantity > 1) {
          params.layout = group?.layout || 'line';
          if (group?.axis) params.layoutAxis = group.axis;
          if (group?.spacing != null) params.spacing = group.spacing;
          if (group?.cols) params.layoutCols = group.cols;
        }
        return {
          message: `Adding ${params.quantity} ${foundType}${params.quantity > 1 ? 's' : ''} to the scene`,
          toolCalls: [
            {
              name: 'add_object',
              parameters: params,
            },
          ],
        };
      }
    }

    // Remove commands
    if (
      lowerCommand.includes('remove') ||
      lowerCommand.includes('delete') ||
      lowerCommand.includes('clear')
    ) {
      if (lowerCommand.includes('all') || lowerCommand.includes('everything')) {
        return {
          message: 'Removing all objects from the scene',
          toolCalls: [
            { name: 'remove_object', parameters: { objectType: 'all' } },
          ],
        };
      }

      const removeMatch = lowerCommand.match(/(?:remove|delete|clear)\s+(\w+)/);
      if (removeMatch) {
        const [, objectType] = removeMatch;
        const normalizedType = this.normalizeObjectType(objectType);
        return {
          message: `Removing ${normalizedType} from the scene`,
          toolCalls: [
            {
              name: 'remove_object',
              parameters: { objectType: normalizedType },
            },
          ],
        };
      }
    }

    // Modify existing objects: "make all cubes orange", "paint spheres red"
    if (
      /\b(make|set|change|paint|colour|color|recolor|tint)\b/.test(
        lowerCommand
      ) &&
      !/\b(add|create|some\s+fire)\b/.test(lowerCommand)
    ) {
      const color = inferColorFromCommand(commandText);
      const typeCandidates = [
        ...this.validShapes,
        ...this.validEffects,
        ...this.validLights,
        'text',
        'hotspot',
        'light',
      ];
      let foundType = null;
      for (const t of typeCandidates) {
        if (
          new RegExp(`\\b${t}s?\\b`, 'i').test(lowerCommand) ||
          (t.endsWith('light') &&
            lowerCommand.includes(t.replace('light', ' light')))
        ) {
          foundType = t;
          break;
        }
      }
      if (color && foundType) {
        const all =
          /\b(?:all|every|each)\b/.test(lowerCommand) ||
          new RegExp(`\\b${foundType}s\\b`, 'i').test(lowerCommand);
        return {
          message: `Making ${all ? 'all ' : ''}${foundType}${all ? 's' : ''} ${color}`,
          toolCalls: [
            {
              name: 'modify_object',
              parameters: {
                objectType: foundType,
                color,
                ...(all ? { all: true } : {}),
              },
            },
          ],
        };
      }
    }

    return {
      message:
        'Command not recognized. Try rephrasing or type "help" for assistance.',
      toolCalls: [],
    };
  }

  /** Pull display string from "add text Hello" / quoted phrases for offline fallback. */
  extractTextContentFallback(original) {
    const quoted = String(original).match(/["']([^"']{1,500})["']/);
    if (quoted) return quoted[1].trim();

    const afterText = String(original).match(
      /\btext\b(?:\s+(?:2d|3d))?\s+(.+)$/i
    );
    if (!afterText) return '';

    const noise = new Set([
      'a',
      'an',
      'the',
      'to',
      'scene',
      'here',
      'please',
      '2d',
      '3d',
      'effect',
      ...Object.keys(NAMED_COLORS).map((k) => k.toLowerCase()),
      'large',
      'big',
      'huge',
      'small',
      'tiny',
      'giant',
      'massive',
    ]);

    const words = afterText[1]
      .trim()
      .split(/\s+/)
      .filter((w) => !noise.has(w.toLowerCase().replace(/[^a-z0-9]/gi, '')));

    return words.join(' ').trim().slice(0, 500);
  }

  extractHotspotFallback(original) {
    const lower = String(original).toLowerCase();
    const quoted = String(original).match(/["']([^"']{1,200})["']/);
    let buttonText = '1';
    let messageText = 'Welcome to spatialgrid';

    const msgMatch = String(original).match(
      /\b(?:with\s+)?(?:message|saying|popup)\s+["']?([^"'.,!]{1,300})["']?/i
    );
    if (msgMatch) messageText = msgMatch[1].trim();

    const labelMatch = String(original).match(
      /\b(?:labelled|labeled|named|called|label)\s+["']?([^"'.,!]{1,80})["']?/i
    );
    if (labelMatch) {
      buttonText = labelMatch[1].trim().slice(0, 40);
    } else if (quoted) {
      buttonText = quoted[1].trim().slice(0, 40);
    } else {
      const after = String(original).match(
        /\bhot\s*-?\s*spot\b\s+(.+)$/i
      );
      if (after) {
        const rest = after[1]
          .replace(
            /\b(?:with\s+)?(?:message|saying|popup)\s+.+$/i,
            ''
          )
          .replace(
            /\b(?:labelled|labeled|named|called|label)\s+/i,
            ''
          )
          .trim();
        const skip = new Set([
          'a',
          'an',
          'the',
          'to',
          'scene',
          'here',
          'please',
          'red',
          'blue',
          'green',
          'circle',
          'rectangle',
        ]);
        const words = rest
          .split(/\s+/)
          .filter((w) => !skip.has(w.toLowerCase()));
        if (words.length) buttonText = words.join(' ').slice(0, 40);
      }
    }

    if (/\bmessage\b/.test(lower) && msgMatch) {
      // already set
    }

    return { buttonText, messageText };
  }

  // Check if command is likely requesting a Sketchfab model
  isSketchfabModelRequest(userCommand) {
    const lowerCommand = userCommand.toLowerCase();
    if (/\btext\b/.test(lowerCommand) || /\bhot\s*-?\s*spot\b/.test(lowerCommand)) {
      return false;
    }
    const sketchfabIndicators = [
      'model',
      '3d',
      'sketchfab',
      'download',
      'character',
      'car',
      'dragon',
      'house',
      'tree',
      'animal',
      'furniture',
      'building',
      'vehicle',
      'asset',
    ];

    return sketchfabIndicators.some((indicator) =>
      lowerCommand.includes(indicator)
    );
  }
}

// Factory function to create interpreter
export const createAIInterpreter = (backendApiUrl = null) => {
  return new AIInterpreter(backendApiUrl);
};
